import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AppEntityTimestampDict, AppEntityTimestampKey } from '@nk/data-entities-lib';
import * as localforage from 'localforage';
import { CacheableFunction, LocalCache } from './caching.service';

interface StableCacheData {
  latestUpdateTimestamp: number;
  data: unknown;
}

function isStableCacheData(item: unknown): item is StableCacheData {
  return (
    !!item &&
    (item as StableCacheData).hasOwnProperty('latestUpdateTimestamp') &&
    (item as StableCacheData).hasOwnProperty('data')
  );
}

const STABLE_CACHE_PREFIX = 'enin' as const;

type StableCacheKey = `${typeof STABLE_CACHE_PREFIX}.${AppEntityTimestampKey}.${string}`;

/**
 * Extended LocalCache class, caches data in browser DB.
 */
@Injectable()
export class StableCache<ClassName, CachedFunction extends CacheableFunction> extends LocalCache<
  ClassName,
  CachedFunction
> {
  /**
   * Extended LocalCache class, caches data in browser DB.
   * @param cacheKey - a key under which data will be stored in the browser DB. Key is supposed to correspond entity table name in the DB
   * @param context - class context
   * @param observableFactory - function to be cached
   * @param _entityTimestampCheckObservable - method to check latest update timestamps in the DB
   */
  constructor(
    protected cacheKey: AppEntityTimestampKey,
    protected context: ClassName,
    protected observableFactory: CachedFunction,
    private _entityTimestampCheckObservable: Observable<AppEntityTimestampDict>,
  ) {
    super(context, observableFactory);
  }

  /**
   * Retrieves data from the browser DB. If data is not found or outdated will get data from API and put into browser DB.
   */
  public getStableCache(...keys: Parameters<CachedFunction>): ReturnType<CachedFunction> {
    const itemKey: StableCacheKey = `${STABLE_CACHE_PREFIX}.${this.cacheKey}.${super.params2key(...keys)}`;
    return this._entityTimestampCheckObservable.pipe(
      switchMap((timestamps) => {
        const latestUpdateTimestamp: number | undefined = timestamps[this.cacheKey]?.valueOf();
        return from(localforage.getItem(itemKey)).pipe(
          switchMap((stableCacheData) => {
            if (
              isStableCacheData(stableCacheData) &&
              latestUpdateTimestamp &&
              latestUpdateTimestamp === stableCacheData.latestUpdateTimestamp &&
              stableCacheData.data
            ) {
              return of(stableCacheData.data);
            }
            return this.fetch(...keys).pipe(
              tap((data) => {
                if (latestUpdateTimestamp) {
                  const stableCacheData: StableCacheData = {
                    latestUpdateTimestamp,
                    data,
                  };
                  void localforage.setItem(itemKey, stableCacheData);
                }
              }),
            );
          }),
        );
      }),
    ) as ReturnType<CachedFunction>;
  }

  public deleteDeepCache(...keys: Parameters<CachedFunction>): Observable<void> {
    const itemKey: StableCacheKey = `${STABLE_CACHE_PREFIX}.${this.cacheKey}.${super.params2key(...keys)}`;
    return from(localforage.removeItem(itemKey));
  }
}
